/*----------------------------------
- AFFILIATES API DOCS CONTENT
----------------------------------*/

/*----------------------------------
- APP
----------------------------------*/

/*----------------------------------
- TYPES
----------------------------------*/

export type AffiliatesApiDocJsonPrimitive = string | number | boolean | null;
export type AffiliatesApiDocJsonValue =
    | AffiliatesApiDocJsonPrimitive
    | AffiliatesApiDocJsonObject
    | AffiliatesApiDocJsonValue[];

export type AffiliatesApiDocJsonObject = {
    [key: string]: AffiliatesApiDocJsonValue;
};

export type AffiliatesApiDocCodeLanguage = 'curl' | 'javascript';
export type AffiliatesApiDocPreviewMode = 'example' | 'typings';
export type AffiliatesApiDocMethod = 'GET' | 'POST';
export type AffiliatesApiDocFieldLocation = 'header' | 'body' | 'path' | 'response';
export type AffiliatesApiDocSectionId = 'overview' | 'generate-signup-link' | 'redemption-flow';

export type AffiliatesApiDocHighlight = {
    label: string;
    value: string;
};

export type AffiliatesApiDocField = {
    name: string;
    type: string;
    required: boolean;
    location: AffiliatesApiDocFieldLocation;
    description: string;
};

export type AffiliatesApiDocStep = {
    title: string;
    description: string;
};

export type AffiliatesApiDocSection = {
    id: AffiliatesApiDocSectionId;
    label: string;
    eyebrow: string;
    title: string;
    summary: string;
    description: string;
    bullets: readonly string[];
    method?: AffiliatesApiDocMethod;
    path?: string;
    requestFields: readonly AffiliatesApiDocField[];
    responseFields: readonly AffiliatesApiDocField[];
};

export type AffiliatesApiDocLanguageOption = {
    id: AffiliatesApiDocCodeLanguage;
    label: string;
};

export type AffiliatesApiDocPreviewOption = {
    id: AffiliatesApiDocPreviewMode;
    label: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const AFFILIATES_API_DOCS_CANONICAL_PATH = '/api/affiliates';
export const AFFILIATES_API_DOCS_LANGUAGES: readonly AffiliatesApiDocLanguageOption[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'JavaScript' },
] as const;

export const AFFILIATES_API_DOCS_PREVIEW_MODES: readonly AffiliatesApiDocPreviewOption[] = [
    { id: 'example', label: 'JSON examples' },
    { id: 'typings', label: 'TypeScript typings' },
] as const;

const AFFILIATES_API_REQUEST_EXAMPLE: AffiliatesApiDocJsonObject = {
    packId: '3b9a1d3d-85ae-4e55-9f9f-26f4444c3d87',
    externalCustomerId: 'edge-client-001',
    externalCustomerEmail: 'client@example.com',
    externalCustomerName: 'EDGE Client 001',
    metadata: {
        affiliate: 'EDGE',
        contractRef: 'EDGE-001',
    },
};

const AFFILIATES_API_REQUEST_TYPINGS = [
    'type CreateAffiliateSignupLinkRequest = {',
    '    packId: string;',
    '    externalCustomerId: string;',
    '    externalCustomerEmail?: string;',
    '    externalCustomerName?: string;',
    '    metadata?: Record<string, string | number | boolean | null | object | unknown[]>;',
    '};',
].join('\n');

const AFFILIATES_API_SIGNUP_LINK_RESPONSE_TYPINGS = [
    'type CreateAffiliateSignupLinkResponse = {',
    '    grantId: string;',
    '    signupUrl: string;',
    "    status: 'active';",
    '    templatePackId: string;',
    '    externalCustomerId: string;',
    '};',
].join('\n');

const AFFILIATES_API_REDEMPTION_FLOW_TYPINGS = [
    'type AffiliateSignupUrlHandoff = {',
    '    signupUrl: string;',
    '    anonymousVisitorRedirect: string;',
    '    redeemedVisitorRedirect: string;',
    '    outcome: string;',
    '};',
].join('\n');

export const AFFILIATES_API_DOCS_CONTENT = {
    hero: {
        eyebrow: 'Affiliates API',
        title: 'Affiliates API',
        description:
            'Give affiliates a documented way to mint a signup URL for their customer, then let that customer redeem an assigned CrossPath pack without a separate purchase step.',
        highlights: [
            { label: 'Auth', value: 'Bearer API key' },
            { label: 'Main endpoint', value: 'POST /api/partners/signup-link' },
            { label: 'Redemption', value: '/partner-signup/:token' },
            { label: 'Access', value: 'Assigned pack activation' },
        ],
    },
    sections: [
        {
            id: 'overview',
            label: 'Overview & auth',
            eyebrow: 'Start here',
            title: 'Authenticate once, then create an affiliate signup link.',
            summary:
                'Affiliates call the signup-link endpoint with a Bearer API key and a template pack id. CrossPath returns a one-customer signup URL that can be opened in the browser.',
            description:
                'The same active grant is reused for the same affiliate, external customer id, and template pack. This keeps affiliate systems idempotent while preserving a single active signup link per assigned customer.',
            bullets: [
                'Use the Affiliates API key in the Authorization header.',
                'One generated link is meant for one external customer and one template pack.',
                'A successful redemption grants access directly instead of starting a separate purchase step.',
            ],
            requestFields: [
                {
                    name: 'Authorization',
                    type: 'Bearer <apiKey>',
                    required: true,
                    location: 'header',
                    description: 'Affiliates API key issued by CrossPath. Requests without a valid Bearer token are rejected.',
                },
            ],
            responseFields: [],
        },
        {
            id: 'generate-signup-link',
            label: 'Generate signup link',
            eyebrow: 'Endpoint',
            title: 'POST /api/partners/signup-link',
            summary:
                'Create or reuse the active assigned signup grant for one external customer and one template pack.',
            description:
                'Use this endpoint from your affiliate backend. It validates the pack, checks for an existing active grant, and returns the URL your customer should open to sign up.',
            bullets: [
                'Required fields: packId and externalCustomerId.',
                'Optional fields: externalCustomerEmail, externalCustomerName, and metadata.',
                'The response includes the stable signupUrl for the active grant.',
            ],
            method: 'POST',
            path: '/api/partners/signup-link',
            requestFields: [
                {
                    name: 'Authorization',
                    type: 'Bearer <apiKey>',
                    required: true,
                    location: 'header',
                    description: 'Affiliates API key generated in CrossPath CP Management.',
                },
                {
                    name: 'packId',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Template UserPack id that should be cloned and redeemed as assigned access.',
                },
                {
                    name: 'externalCustomerId',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Your stable external id for the customer receiving the assigned access.',
                },
                {
                    name: 'externalCustomerEmail',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Optional customer email kept on the grant for auditing and support.',
                },
                {
                    name: 'externalCustomerName',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Optional customer name stored alongside the grant.',
                },
                {
                    name: 'metadata',
                    type: 'object',
                    required: false,
                    location: 'body',
                    description: 'Optional affiliate-side metadata such as contract references or internal notes.',
                },
            ],
            responseFields: [
                {
                    name: 'grantId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'CrossPath id for the signup grant.',
                },
                {
                    name: 'signupUrl',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Public URL the affiliate customer should open to redeem the assigned pack.',
                },
                {
                    name: 'status',
                    type: 'active',
                    required: true,
                    location: 'response',
                    description: 'Status of the signup grant returned by the endpoint.',
                },
                {
                    name: 'templatePackId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'The original template pack id tied to the grant.',
                },
                {
                    name: 'externalCustomerId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'The same affiliate-side customer id provided in the request.',
                },
            ],
        },
        {
            id: 'redemption-flow',
            label: 'Redemption flow',
            eyebrow: 'Browser handoff',
            title: 'What happens when the affiliate customer opens signupUrl',
            summary:
                'The returned signupUrl points to /partner-signup/:token. CrossPath stores the grant context, then redirects the customer to signup, login, or the final in-app destination depending on the grant state and session state.',
            description:
                'This route is public and browser-oriented. It is not a second API endpoint for affiliate systems; it is the handoff URL that customers use to redeem the assigned pack.',
            bullets: [
                'Anonymous visitors are redirected to /auth/signup/:templatePackId for first redemption.',
                'Already redeemed grants may redirect to /auth/login/:templatePackId instead.',
                'Once signup or login completes, the customer lands with an active assigned pack and no separate purchase step.',
            ],
            method: 'GET',
            path: '/partner-signup/:token',
            requestFields: [
                {
                    name: 'token',
                    type: 'string',
                    required: true,
                    location: 'path',
                    description: 'Opaque signup grant token returned inside signupUrl.',
                },
            ],
            responseFields: [],
        },
    ] as const satisfies readonly AffiliatesApiDocSection[],
    flowSteps: [
        {
            title: 'Generate the assigned link',
            description: 'Your backend calls POST /api/partners/signup-link with a template pack id and one external customer id.',
        },
        {
            title: 'Send signupUrl to the customer',
            description: 'CrossPath returns a browser URL that already knows which assigned pack should be redeemed.',
        },
        {
            title: 'Let CrossPath handle the redemption',
            description: 'The customer signs up or logs in, then receives the assigned pack immediately without a separate purchase step.',
        },
    ] as const satisfies readonly AffiliatesApiDocStep[],
    redemptionOutcomes: [
        {
            title: 'Anonymous customer',
            description: 'Redirected to /auth/signup/:templatePackId to create the account before the pack is redeemed.',
        },
        {
            title: 'Existing customer, not logged in',
            description: 'Redirected to /auth/login/:templatePackId so the same assigned grant can be redeemed after login.',
        },
        {
            title: 'Already logged in',
            description: 'The grant is redeemed immediately and the customer is redirected straight into the app.',
        },
    ] as const satisfies readonly AffiliatesApiDocStep[],
} as const;

/*----------------------------------
- HELPERS
----------------------------------*/

export const formatAffiliatesApiJson = (value: AffiliatesApiDocJsonValue) => JSON.stringify(value, null, 4);

export const buildAffiliatesApiRequestExample = (): AffiliatesApiDocJsonObject => ({
    ...AFFILIATES_API_REQUEST_EXAMPLE,
});

export const buildAffiliatesApiRequestTypings = () => AFFILIATES_API_REQUEST_TYPINGS;

export const buildAffiliatesApiResponseExample = (baseUrl: string): AffiliatesApiDocJsonObject => ({
    grantId: 'psg_01JZ4J6H2T8Y1R3P5N7K9M1Q2W',
    signupUrl: `${baseUrl}/partner-signup/psg_demo_token_01`,
    status: 'active',
    templatePackId: '3b9a1d3d-85ae-4e55-9f9f-26f4444c3d87',
    externalCustomerId: 'edge-client-001',
});

export const buildAffiliatesApiResponseTypings = (
    sectionId: Extract<AffiliatesApiDocSectionId, 'generate-signup-link' | 'redemption-flow'>,
): string => {
    if (sectionId === 'generate-signup-link') return AFFILIATES_API_SIGNUP_LINK_RESPONSE_TYPINGS;

    return AFFILIATES_API_REDEMPTION_FLOW_TYPINGS;
};

export const buildAffiliatesApiAuthSnippet = () => [
    'Authorization: Bearer Affiliate-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    'Content-Type: application/json',
].join('\n');

export const buildAffiliatesApiCurlSnippet = (baseUrl: string) => [
    `curl --request POST '${baseUrl}/api/partners/signup-link' \\`,
    "  --header 'Authorization: Bearer Affiliate-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' \\",
    "  --header 'Content-Type: application/json' \\",
    `  --data '${JSON.stringify(AFFILIATES_API_REQUEST_EXAMPLE)}'`,
].join('\n');

export const buildAffiliatesApiJavaScriptSnippet = (baseUrl: string) => [
    `const response = await fetch('${baseUrl}/api/partners/signup-link', {`,
    "    method: 'POST',",
    '    headers: {',
    "        Authorization: 'Bearer Affiliate-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',",
    "        'Content-Type': 'application/json',",
    '    },',
    `    body: JSON.stringify(${formatAffiliatesApiJson(AFFILIATES_API_REQUEST_EXAMPLE)}),`,
    '});',
    '',
    'const data = await response.json();',
    'console.log(data.signupUrl);',
].join('\n');

export const buildAffiliatesApiRedemptionSnippet = (baseUrl: string) => [
    `GET ${baseUrl}/partner-signup/psg_demo_token_01`,
    '',
    'Anonymous visitor  -> /auth/signup/:templatePackId',
    'Redeemed visitor   -> /auth/login/:templatePackId or direct app redirect',
    'Successful auth    -> assigned pack activated without purchase step',
].join('\n');
